import { useRef, useState, type ChangeEvent } from 'react';
import { Upload } from 'lucide-react';
import { useNotify } from 'react-admin';
import * as XLSX from 'xlsx';
import { Button } from '../ui/button.tsx';
import { Label } from '../ui/label.tsx';

declare const process: {
	env: {
		REACT_APP_API_URL?: string;
	};
};

export interface COATemplateItem {
	id?: number;
	batchNumber?: string;
	parameter: string;
	classification: string;
	testMethod: string;
	uom: string;
}

export interface COATemplate {
	id: string;
	batchNumber: string;
	productDescription: string;
	manufacturer?: string | null;
	productType?: string | null;
	countryOfOrigin?: string | null;
	specNumber?: string | null;
	reportNumber?: string | null;
	sampleSubmissionRefNumber?: string | null;
	sampleReceivedDate?: string | null;
	packaging?: string | null;
	testingPurpose?: string | null;
	client?: string | null;
	manufactureDate?: string | null;
	expiryDate?: string | null;
	description?: string;
	preparedBy?: string;
	preparedByTitle?: string;
	preparedBySignature?: string;
	preparedByDate?: string;
	releaseBy?: string;
	releaseByTitle?: string;
	releaseBySignature?: string;
	releaseByDate?: string;
	items: COATemplateItem[];
}

interface TemplateImportProps {
	onImported: () => Promise<void> | void;
	apiUrl?: string;
	className?: string;
}

type TemplateImportType = 'normal' | 'special';

const TEMPLATE_SHEET_NAME_CANDIDATES = ['COATemplate', 'CoA Template', 'Analytical Result'];
const SUMMARY_ROW_SKIP_COUNT = 8;
const SPECIAL_TEMPLATE_SUMMARY_ROW_SKIP_COUNT = 11;

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[_\s\-/()]+/g, '');

const extractClassificationName = (value: string): string => {
	const text = String(value ?? '').trim();
	if (!text) return 'General';

	const firstLine = text.split(/\r?\n/)[0]?.trim() || text;
	return firstLine.replace(/[\s:;，、。]+$/g, '') || 'General';
};

const isParameterHeader = (value: string): boolean => {
	const normalized = normalizeHeader(value);
	return normalized === 'parameter' || normalized.includes('parameter');
};

const extractInlineLabelValue = (value: unknown): string => {
	const text = String(value ?? '').trim();
	if (!text) return '';

	const separatorIndex = text.search(/[:：]/);
	if (separatorIndex < 0) return '';

	return text.slice(separatorIndex + 1).trim();
};

const isLabeledCell = (value: unknown): boolean => {
	const text = String(value ?? '').trim();
	return /^[^:：\r\n]{1,80}[:：]/.test(text);
};

const toIsoDate = (value: unknown): string => {
	if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
	if (typeof value === 'number') {
		if (!Number.isFinite(value) || value <= 0) return '';
		const jsDate = XLSX.SSF.parse_date_code(value);
		if (jsDate) {
			const d = new Date(jsDate.y, jsDate.m - 1, jsDate.d);
			if (d.getFullYear() < 1900) return '';
			return d.toISOString().slice(0, 10);
		}
	}
	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) return '';
		const normalized = trimmed.replace(/\./g, '/').replace(/-/g, '/');
		const parts = normalized.split('/').map((part) => part.trim());
		if (parts.length === 3) {
			const y = Number(parts[0]);
			const m = Number(parts[1]);
			const d = Number(parts[2]);
			if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
				const date = new Date(y, m - 1, d);
				if (!Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
			}
		}
		const date = new Date(trimmed);
		if (!Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
	}
	return '';
};

const getCellDisplayText = (cell: XLSX.CellObject | undefined): string => {
	if (!cell) return '';
	const formattedValue = typeof cell.w === 'string' ? cell.w.trim() : '';
	if (formattedValue) return formattedValue;

	const rawValue = cell.v;
	if (rawValue === null || rawValue === undefined) return '';
	return String(rawValue).trim();
};

const worksheetToDisplayRows = (sheet: XLSX.WorkSheet): string[][] => {
	const ref = sheet['!ref'];
	if (!ref) return [];

	const range = XLSX.utils.decode_range(ref);
	const colsMeta = sheet['!cols'] || [];

	const visibleCols: number[] = [];
	for (let c = range.s.c; c <= range.e.c; c++) {
		const colMeta = colsMeta[c];
		const isHidden = colMeta && colMeta.hidden === true;
		const isZeroWidth = colMeta && (colMeta.wpx === 0 || colMeta.wch === 0 || colMeta.width === 0);
		if (isHidden || isZeroWidth) continue;
		visibleCols.push(c);
	}

	const rows: string[][] = [];
	const rowsMeta = sheet['!rows'] || [];

	for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
		const rowMeta = rowsMeta[rowIndex];
		if (rowMeta && rowMeta.hidden === true) {
			continue;
		}

		const row: string[] = [];
		for (const columnIndex of visibleCols) {
			const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
			row.push(getCellDisplayText(sheet[cellAddress] as XLSX.CellObject | undefined));
		}
		rows.push(row);
	}

	return rows;
};

const normalizeSheetName = (value: string) => normalizeHeader(value);

// requireClassification: normal templates require chemistry/parameter in header;
// special templates only need method + uom (classification name appears in col 0 instead).
const findCoATemplateHeaderRowIndex = (rows: any[][], startRow = SUMMARY_ROW_SKIP_COUNT, requireClassification = true): number => {
	for (let r = startRow; r < rows.length; r++) {
		const row = rows[r] || [];
		const normalizedCells = row.map((cell) => normalizeHeader(String(cell ?? ''))).filter(Boolean);
		const rowText = normalizedCells.join(' ');
		const hasMethod = normalizedCells.some((cell) => (cell || '').includes('testmethod') || (cell || '').includes('method'));
		const hasUom = normalizedCells.some((cell) => (cell || '').includes('uom') || (cell || '').includes('unit'));
		const hasChemistry = normalizedCells.some((cell) => cell === 'chemistry' || cell.includes('chemistry'));
		const hasParameter = normalizedCells.some((cell) => cell === 'parameter' || cell.includes('parameter'));

		if ((hasChemistry || hasParameter || !requireClassification) && hasMethod && hasUom) {
			return r;
		}

		if (rowText.includes('chemistry') && rowText.includes('method') && rowText.includes('uom')) {
			return r;
		}
	}

	return -1;
};

const findLabeledValue = (rows: any[][], label: string, startRow = 0, endRow = rows.length - 1): unknown => {
	const target = normalizeHeader(label);
	const rowLimit = Math.max(0, Math.min(rows.length - 1, endRow));
	for (let r = Math.max(0, startRow); r <= rowLimit; r++) {
		const row = rows[r] || [];
		for (let c = 0; c < row.length; c++) {
			const current = normalizeHeader(String(row[c] ?? ''));
			if (!current || !current.includes(target)) continue;

			const inlineValue = extractInlineLabelValue(row[c]);
			if (inlineValue) return inlineValue;

			const sameRowCandidates = [row[c + 1], row[c + 2], row[c + 3], row[c + 4]];
			for (const value of sameRowCandidates) {
				if (isLabeledCell(value)) break;
				if (String(value ?? '').trim()) return value;
			}

			for (let rr = r + 1; rr <= Math.min(r + 2, rowLimit); rr++) {
				const belowRow = rows[rr] || [];
				for (let cc = c; cc <= Math.min(c + 3, belowRow.length - 1); cc++) {
					const value = belowRow[cc];
					if (String(value ?? '').trim()) return value;
				}
			}

			const inlineParts = String(row[c] ?? '').split(/[:：]/).slice(1).map((part) => part.trim()).filter(Boolean);
			if (inlineParts.length > 0) return inlineParts.join(': ');
		}
	}
	return '';
};

const findLabeledValueInRow = (row: any[], label: string, startColumn = 0): unknown => {
	const target = normalizeHeader(label);
	for (let c = Math.max(0, startColumn); c < row.length; c++) {
		const current = normalizeHeader(String(row[c] ?? ''));
		if (!current || !current.includes(target)) continue;

		const inlineValue = extractInlineLabelValue(row[c]);
		if (inlineValue) return inlineValue;

		for (let next = c + 1; next <= Math.min(c + 4, row.length - 1); next++) {
			const value = row[next];
			if (isLabeledCell(value)) break;
			if (String(value ?? '').trim()) return value;
		}
	}

	return '';
};

const collectFooterDescription = (rows: string[][], startRow: number): string => {
	const descriptionRows: string[] = [];

	for (let r = Math.max(0, startRow); r < rows.length; r++) {
		const row = rows[r] || [];
		const rowText = row.map((cell) => String(cell ?? '').trim()).filter(Boolean).join(' ').trim();
		if (!rowText) {
			if (descriptionRows.length > 0) break;
			continue;
		}

		const normalizedText = normalizeHeader(rowText);
		if (
			normalizedText.includes('preparedby') ||
			normalizedText.includes('releasedby') ||
			normalizedText.includes('releaseby') ||
			normalizedText.includes('signature') ||
			normalizedText === 'title' ||
			normalizedText.startsWith('titlequality')
		) {
			break;
		}

		descriptionRows.push(rowText);
	}

	return descriptionRows.join('\n');
};

const getTargetSheet = (workbook: XLSX.WorkBook): XLSX.WorkSheet => {
	for (const candidateName of TEMPLATE_SHEET_NAME_CANDIDATES) {
		const exactSheetName = workbook.SheetNames.find((sheetName) => normalizeSheetName(sheetName) === normalizeSheetName(candidateName));
		if (!exactSheetName) continue;

		const candidateSheet = workbook.Sheets[exactSheetName];
		if (candidateSheet) return candidateSheet;
	}

	for (const sheetName of workbook.SheetNames) {
		const sheet = workbook.Sheets[sheetName];
		if (!sheet) continue;
		const rows = worksheetToDisplayRows(sheet);
		if (findCoATemplateHeaderRowIndex(rows) >= 0) {
			return sheet;
		}
	}

	throw new Error(`Worksheet not found. Expected one of: ${TEMPLATE_SHEET_NAME_CANDIDATES.join(', ')}`);
};

export default function TemplateImport({ onImported, apiUrl, className }: TemplateImportProps) {
	const API_URL = apiUrl || process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
	const notify = useNotify();
	const importFileInputRef = useRef<HTMLInputElement | null>(null);

	const [importModalOpen, setImportModalOpen] = useState(false);
	const [importTemplateType, setImportTemplateType] = useState<TemplateImportType>('normal');
	const [importFile, setImportFile] = useState<File | null>(null);
	const [importing, setImporting] = useState(false);

	const parseCoATemplateSheet = (sheet: XLSX.WorkSheet, isSpecialTemplate = false): Omit<COATemplate, 'id'> => {
		const rows = worksheetToDisplayRows(sheet);
		const skipCount = isSpecialTemplate ? SPECIAL_TEMPLATE_SUMMARY_ROW_SKIP_COUNT : SUMMARY_ROW_SKIP_COUNT;
		const headerRowIndex = findCoATemplateHeaderRowIndex(rows, skipCount, !isSpecialTemplate);
		const summaryRowEndIndex = Math.max(0, Math.min(headerRowIndex - 1, skipCount - 1));

		if (headerRowIndex < 0) {
			throw new Error('Unable to locate the COATemplate table header row.');
		}

		const headerRow = rows[headerRowIndex] || [];
		const headerMap = headerRow.reduce<Record<string, number>>((acc, cell, index) => {
			const normalized = normalizeHeader(String(cell ?? ''));
			if (normalized) acc[normalized] = index;
			return acc;
		}, {});

		const parameterIdx = 0;
		const uomIdx = headerMap.uom ?? headerMap.unit ?? 1;
		const testResultIdx = headerMap.testresult ?? headerMap.result ?? 2;
		const testMethodIdx = headerMap.testmethod ?? headerMap.method ?? 3;

		if (testMethodIdx < 0 || uomIdx < 0) {
			throw new Error('Unable to locate COATemplate columns (Test Method/UOM).');
		}

		const productDescriptionRaw = isSpecialTemplate
			? (
				findLabeledValue(rows, 'Product Name', 0, summaryRowEndIndex) ||
				findLabeledValue(rows, 'Product Description', 0, summaryRowEndIndex)
			)
			: findLabeledValue(rows, 'Product Description', 0, summaryRowEndIndex);
		const manufacturerRaw = findLabeledValue(rows, 'Manufacturer', 0, summaryRowEndIndex);
		const productTypeRaw = findLabeledValue(rows, 'Product Type', 0, summaryRowEndIndex);
		const countryOfOriginRaw = findLabeledValue(rows, 'Country of Origin', 0, summaryRowEndIndex);
		const specNumberRaw = isSpecialTemplate
			? (
				findLabeledValue(rows, 'Product Code', 0, summaryRowEndIndex) ||
				findLabeledValue(rows, 'Spec Number', 0, summaryRowEndIndex)
			)
			: findLabeledValue(rows, 'Spec Number', 0, summaryRowEndIndex);
		const reportNumberRaw = findLabeledValue(rows, 'Report Number', 0, summaryRowEndIndex);
		const batchNumberRaw = findLabeledValue(rows, 'Batch Number', 0, summaryRowEndIndex);
		const sampleReceivedDateRaw = findLabeledValue(rows, 'Sample Received Date', 0, summaryRowEndIndex);
		const manufactureDateRaw = findLabeledValue(rows, 'Manufacture Date', 0, summaryRowEndIndex);
		const expiryDateRaw = findLabeledValue(rows, 'Expiry Date', 0, summaryRowEndIndex);

		const items: COATemplateItem[] = [];
		const firstHeaderCell = String(headerRow[parameterIdx] ?? '').trim();
		let currentClassification = firstHeaderCell && !isParameterHeader(firstHeaderCell)
			? extractClassificationName(firstHeaderCell)
			: 'General';
		let emptyRowStreak = 0;
		let footerStartIndex = rows.length;

		for (let i = headerRowIndex + 1; i < rows.length; i++) {
			const row = rows[i] || [];
			const parameter = parameterIdx >= 0 ? String(row[parameterIdx] ?? '').trim() : '';
			const testMethod = testMethodIdx >= 0 ? String(row[testMethodIdx] ?? '').trim() : '';
			const uom = uomIdx >= 0 ? String(row[uomIdx] ?? '').trim() : '';
			const testResult = testResultIdx >= 0 ? String(row[testResultIdx] ?? '').trim() : '';

			const firstCell = String(row[0] ?? '').trim();

			const normalizedUom = normalizeHeader(uom || '');
			const normalizedMethod = normalizeHeader(testMethod || '');
			const normalizedResult = normalizeHeader(testResult || '');

			const looksLikeHeaderInColumns = [
				(normalizedUom || '').includes('uom') || (normalizedUom || '').includes('unit'),
				(normalizedMethod || '').includes('testmethod') || (normalizedMethod || '').includes('method'),
				(normalizedResult || '').includes('testresult') || (normalizedResult || '').includes('result'),
			].some(Boolean);
			const onlyFirstCellHasText = Boolean(firstCell && !testMethod && !uom && !testResult);

			if (firstCell && isParameterHeader(firstCell) && looksLikeHeaderInColumns) {
				continue;
			}

			if (firstCell && !isParameterHeader(firstCell) && (looksLikeHeaderInColumns || onlyFirstCellHasText)) {
				currentClassification = extractClassificationName(firstCell);
				continue;
			}

			const normalizedParameter = normalizeHeader(parameter);
			const isMetaRow = [
				'productdescription',
				'batchnumber',
				'specnumber',
				'samplesubmissionrefnumber',
				'manufacturedate',
				'expirydate',
			].includes(normalizedParameter);

			if (!parameter && !testMethod && !uom && !testResult) {
				emptyRowStreak += 1;
				if (items.length > 0 && emptyRowStreak >= 1) {
					footerStartIndex = i + 1;
					break;
				}
				continue;
			}

			emptyRowStreak = 0;

			if (isMetaRow) {
				continue;
			}

			items.push({
				parameter: parameter || testMethod || 'N/A',
				classification: currentClassification,
				testMethod: testMethod || parameter || 'N/A',
				uom: uom || '-',
			});
		}

		const footerSearchStart = Math.max(0, footerStartIndex);
		const packagingRaw =
			findLabeledValue(rows, 'Packaging', footerSearchStart) ||
			findLabeledValue(rows, 'Packaging', 0, summaryRowEndIndex);
		const testingPurposeRaw =
			findLabeledValue(rows, 'Testing Purpose', footerSearchStart) ||
			findLabeledValue(rows, 'Testing Purpose', 0, summaryRowEndIndex);
		const clientRaw =
			findLabeledValue(rows, 'Client', footerSearchStart) ||
			findLabeledValue(rows, 'Client', 0, summaryRowEndIndex);
		const descriptionRaw = collectFooterDescription(rows, footerSearchStart);
		const preparedByRaw = findLabeledValue(rows, 'Prepared by', footerSearchStart);
		const preparedByTitleRaw = findLabeledValue(rows, 'Title', footerSearchStart);
		const preparedBySignatureRaw = findLabeledValue(rows, 'Signature', footerSearchStart);
		const preparedByDateRaw =
			findLabeledValue(rows, 'Prepared By Date', footerSearchStart) ||
			findLabeledValue(rows, 'Prepared Date', footerSearchStart);
		const releaseByRaw =
			findLabeledValue(rows, 'Released by', footerSearchStart) ||
			findLabeledValue(rows, 'Release by', footerSearchStart);
		const releaseByTitleRaw = (() => {
			for (let r = footerSearchStart; r < rows.length; r++) {
				const row = rows[r] || [];
				const releaseColumnIndex = row.findIndex((cell) => {
					const normalized = normalizeHeader(String(cell ?? ''));
					return normalized.includes('releasedby') || normalized.includes('releaseby');
				});

				if (releaseColumnIndex < 0) continue;

				for (let rr = r + 1; rr <= Math.min(r + 2, rows.length - 1); rr++) {
					const titleValue = findLabeledValueInRow(rows[rr] || [], 'Title', releaseColumnIndex);
					if (String(titleValue ?? '').trim()) return titleValue;
				}
			}

			return '';
		})();
		const releaseBySignatureRaw = (() => {
			for (let r = footerSearchStart; r < rows.length; r++) {
				const row = rows[r] || [];
				const releaseColumnIndex = row.findIndex((cell) => {
					const normalized = normalizeHeader(String(cell ?? ''));
					return normalized.includes('releasedby') || normalized.includes('releaseby');
				});

				if (releaseColumnIndex < 0) continue;

				for (let rr = r + 1; rr <= Math.min(r + 3, rows.length - 1); rr++) {
					const signatureValue = findLabeledValueInRow(rows[rr] || [], 'Signature', releaseColumnIndex);
					if (String(signatureValue ?? '').trim()) return signatureValue;
				}
			}

			return '';
		})();
		const releaseByDateRaw =
			findLabeledValue(rows, 'Release By Date', footerSearchStart) ||
			findLabeledValue(rows, 'Released Date', footerSearchStart) ||
			findLabeledValue(rows, 'Release Date', footerSearchStart);

		return {
			productDescription: String(productDescriptionRaw ?? '').trim(),
			manufacturer: String(manufacturerRaw ?? '').trim(),
			productType: String(productTypeRaw ?? '').trim(),
			countryOfOrigin: String(countryOfOriginRaw ?? '').trim(),
			specNumber: String(specNumberRaw ?? '').trim(),
			reportNumber: String(reportNumberRaw ?? '').trim(),
			batchNumber: String(batchNumberRaw ?? '').trim(),
			sampleSubmissionRefNumber: '',
			sampleReceivedDate: toIsoDate(sampleReceivedDateRaw),
			packaging: String(packagingRaw ?? '').trim(),
			testingPurpose: String(testingPurposeRaw ?? '').trim(),
			client: String(clientRaw ?? '').trim(),
			manufactureDate: toIsoDate(manufactureDateRaw),
			expiryDate: toIsoDate(expiryDateRaw),
			description: String(descriptionRaw ?? '').trim(),
			preparedBy: String(preparedByRaw ?? '').trim(),
			preparedByTitle: String(preparedByTitleRaw ?? '').trim(),
			preparedBySignature: String(preparedBySignatureRaw ?? '').trim(),
			preparedByDate: toIsoDate(preparedByDateRaw),
			releaseBy: String(releaseByRaw ?? '').trim(),
			releaseByTitle: String(releaseByTitleRaw ?? '').trim(),
			releaseBySignature: String(releaseBySignatureRaw ?? '').trim(),
			releaseByDate: toIsoDate(releaseByDateRaw),
			items,
		};
	};

	const handleSelectImportFile = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] || null;
		setImportFile(file);
	};

	const handleConfirmImport = async () => {
		const file = importFile;
		if (!file) return;

		setImporting(true);
		try {
			const isSpecialTemplate = importTemplateType === 'special';
			const arrayBuffer = await file.arrayBuffer();
			const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });
			const targetSheet = getTargetSheet(workbook);
			const parsedSpec = parseCoATemplateSheet(targetSheet, isSpecialTemplate);

			parsedSpec.sampleSubmissionRefNumber = String(
				findLabeledValue(worksheetToDisplayRows(targetSheet), 'Sample Submission Ref Number', 0, SUMMARY_ROW_SKIP_COUNT - 1) ||
				findLabeledValue(worksheetToDisplayRows(targetSheet), 'Sample Submission Ref Number (s)', 0, SUMMARY_ROW_SKIP_COUNT - 1) ||
				parsedSpec.sampleSubmissionRefNumber ||
				''
			).trim();

			const missingFields: string[] = [];
			if (!parsedSpec.batchNumber) missingFields.push('Batch Number');
			if (!parsedSpec.productDescription) missingFields.push('Product Description');
			if (parsedSpec.items.length === 0) missingFields.push('Template Items');

			if (missingFields.length > 0) {
				throw new Error(`Unable to parse required fields from COATemplate tab: ${missingFields.join(', ')}`);
			}

			const dto = {
				BatchNumber: parsedSpec.batchNumber,
				ProductDescription: parsedSpec.productDescription,
				Manufacturer: parsedSpec.manufacturer || null,
				ProductType: parsedSpec.productType || null,
				CountryOfOrigin: parsedSpec.countryOfOrigin || null,
				SpecNumber: parsedSpec.specNumber || null,
				ReportNumber: parsedSpec.reportNumber || null,
				SampleSubmissionRefNumber: parsedSpec.sampleSubmissionRefNumber || null,
				SampleReceivedDate: parsedSpec.sampleReceivedDate || null,
				Packaging: parsedSpec.packaging || null,
				TestingPurpose: parsedSpec.testingPurpose || null,
				Client: parsedSpec.client || null,
				ManufactureDate: parsedSpec.manufactureDate || null,
				ExpiryDate: parsedSpec.expiryDate || null,
				Description: parsedSpec.description,
				PreparedBy: parsedSpec.preparedBy,
				PreparedByTitle: parsedSpec.preparedByTitle,
				PreparedBySignature: parsedSpec.preparedBySignature,
				PreparedByDate: parsedSpec.preparedByDate || null,
				ReleaseBy: parsedSpec.releaseBy,
				ReleaseByTitle: parsedSpec.releaseByTitle,
				ReleaseBySignature: parsedSpec.releaseBySignature,
				ReleaseByDate: parsedSpec.releaseByDate || null,
				Items: parsedSpec.items.map((item) => ({
					Parameter: item.parameter,
					Classification: item.classification,
					TestMethod: item.testMethod,
					Uom: item.uom,
				})),
			};

			const token = localStorage.getItem('token');
			const response = await fetch(`${API_URL}/COATemplates`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify(dto),
			});

			if (!response.ok) {
				const text = await response.text();
				throw new Error(text || `HTTP ${response.status}`);
			}

			notify(`Import success: ${parsedSpec.items.length} template items imported`, { type: 'success' });
			await onImported();

			setImportModalOpen(false);
			setImportFile(null);
			if (importFileInputRef.current) {
				importFileInputRef.current.value = '';
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Unknown error';
			notify(`Import failed: ${message}`, { type: 'error' });
		} finally {
			setImporting(false);
		}
	};

	const handleCloseModal = () => {
		if (importing) return;
		setImportModalOpen(false);
		setImportFile(null);
		if (importFileInputRef.current) {
			importFileInputRef.current.value = '';
		}
	};

	return (
		<div className={className}>
			<Button
				type="button"
				variant="outline"
				className="border-blue-300 text-blue-700 hover:bg-blue-50"
				onClick={() => {
					setImportTemplateType('normal');
					setImportFile(null);
					setImportModalOpen(true);
				}}
			>
				<Upload className="mr-2 size-4" />
				Import
			</Button>

			{importModalOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
					<div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
						<div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
							<h3 className="text-lg font-semibold text-slate-900">Import Template</h3>
							<Button type="button" variant="ghost" onClick={handleCloseModal}>
								Close
							</Button>
						</div>

						<div className="space-y-4 px-5 py-4">
							<div className="space-y-2">
								<Label className="text-sm text-slate-700">1. Select Template Type</Label>
								<select
									className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
									value={importTemplateType}
									onChange={(e) => setImportTemplateType(e.target.value as TemplateImportType)}
									disabled={importing}
								>
									<option value="normal">Normal Template</option>
									<option value="special">Special Template</option>
								</select>
								<p className="text-xs text-slate-500">
									{importTemplateType === 'special'
										? 'Special template: metadata is read from the first 11 rows of the COATemplate sheet.'
										: 'Normal template: standard COATemplate layout.'}
								</p>
							</div>

							<div className="space-y-2">
								<Label className="text-sm text-slate-700">2. Choose File</Label>
								<input
									ref={importFileInputRef}
									type="file"
									accept=".xlsx,.xls"
									className="sr-only"
									onChange={handleSelectImportFile}
									disabled={importing}
								/>
								<div className="flex items-center gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={() => importFileInputRef.current?.click()}
										disabled={importing}
									>
										Choose File
									</Button>
									<span className="text-sm text-slate-600">
										{importFile ? importFile.name : 'No file selected'}
									</span>
								</div>
							</div>
						</div>

						<div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
							<Button
								type="button"
								variant="ghost"
								onClick={handleCloseModal}
								disabled={importing}
							>
								Cancel
							</Button>
							<Button
								type="button"
								className="bg-blue-600 text-white hover:bg-blue-700"
								onClick={handleConfirmImport}
								disabled={importing || !importFile}
							>
								{importing ? 'Importing...' : 'Import and Parse'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
